/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  PreviewService,
  InternalRatingsService,
  type TPreview,
  type TPreviewServiceGetContentPreviewParamsV3,
  type TPreviewServiceGetOfferPreviewParamsV5,
  type TPreviewServiceGetOfferPreviewParamsV4,
  type TPreviewServiceGetRecordingPreviewParamsV3,
  type TPreviewServiceGetUnifiedCollectionPreviewParams,
  type TPreviewServiceGetVodOfferPreviewParamsV2,
  type TServicesParams
} from "@tivocorp/data-domain-lib";

import type { IPreviewInteractor } from "../../interfaces";
import type { TNetworkOptions } from "../../types";
import { Log } from "../../logging/Log";
import { ApplicationFeatureArea } from "../../enums";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor } from "../base";
import { ServiceLocator } from "../base/ServiceLocator";

/**
 * Shared, atomic interactor wrapping {@link PreviewService}.
 *
 * Multiple feature interactors (Playback, Guide, ContentDetails, Carousel)
 * compose this rather than reaching for `PreviewService` themselves.
 * The {@link ApplicationFeatureArea} is fixed at construction time so the
 * resulting `applicationFeatureArea` request header matches the calling
 * feature's context.
 *
 * Stateless: no service instance is cached; a fresh `PreviewService` is
 * built per call via {@link ServiceLocator}.
 */
export class PreviewInteractor
  extends BaseInteractor
  implements IPreviewInteractor
{
  private static readonly TAG: string = "PreviewInteractor";

  /**
   * @param mFeatureArea - ApplicationFeatureArea reported in the
   *                       request header for every call made by this
   *                       instance. Defaults to {@link ApplicationFeatureArea.ALL}.
   */
  public constructor(
    private readonly mFeatureArea: ApplicationFeatureArea = ApplicationFeatureArea.ALL
  ) {
    super();
  }

  public async fetchOfferPreviewV4(
    params: TPreviewServiceGetOfferPreviewParamsV4,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<TPreview> {
    return this.executeGuarded(
      "fetchOfferPreviewV4",
      async (guardedSignal: AbortSignal): Promise<TPreview> => {
        Log.d(PreviewInteractor.TAG, "fetchOfferPreviewV4() called");
        const service: PreviewService = ServiceLocator.getInstance(
          PreviewService,
          await this.buildServicesParams(
            TIVO_SERVICES.PreviewService,
            this.mFeatureArea
          )
        );
        return await service.fetchOfferPreviewV4(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchOfferPreviewV5(
    params: TPreviewServiceGetOfferPreviewParamsV5,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<TPreview> {
    return this.executeGuarded(
      "fetchOfferPreviewV5",
      async (guardedSignal: AbortSignal): Promise<TPreview> => {
        Log.d(PreviewInteractor.TAG, "fetchOfferPreviewV5() called");
        const previewService = new PreviewService(
          await this.buildServicesParams(
            TIVO_SERVICES.PreviewService,
            this.mFeatureArea
          )
        );
        const res: TPreview = await previewService.fetchOfferPreviewV5(
          params,
          isForced,
          guardedSignal
        );
        return res;
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchContentPreviewV3(
    params: TPreviewServiceGetContentPreviewParamsV3,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<TPreview> {
    return this.executeGuarded(
      "fetchContentPreviewV3",
      async (guardedSignal: AbortSignal): Promise<TPreview> => {
        Log.d(PreviewInteractor.TAG, "fetchContentPreviewV3() called");
        const service: PreviewService = ServiceLocator.getInstance(
          PreviewService,
          await this.buildServicesParams(
            TIVO_SERVICES.PreviewService,
            this.mFeatureArea
          )
        );
        return await service.fetchContentPreviewV3(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchUnifiedCollectionPreview(
    params: TPreviewServiceGetUnifiedCollectionPreviewParams,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<TPreview> {
    return this.executeGuarded(
      "fetchUnifiedCollectionPreview",
      async (guardedSignal: AbortSignal): Promise<TPreview> => {
        Log.d(PreviewInteractor.TAG, "fetchUnifiedCollectionPreview() called");
        const service: PreviewService = ServiceLocator.getInstance(
          PreviewService,
          await this.buildServicesParams(
            TIVO_SERVICES.PreviewService,
            this.mFeatureArea
          )
        );
        return await service.fetchUnifiedCollectionPreview(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchRecordingPreviewV3(
    params: TPreviewServiceGetRecordingPreviewParamsV3,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<TPreview> {
    return this.executeGuarded(
      "fetchRecordingPreviewV3",
      async (guardedSignal: AbortSignal): Promise<TPreview> => {
        Log.d(PreviewInteractor.TAG, "fetchRecordingPreviewV3() called");
        const service: PreviewService = ServiceLocator.getInstance(
          PreviewService,
          await this.buildServicesParams(
            TIVO_SERVICES.PreviewService,
            this.mFeatureArea
          )
        );
        return await service.fetchRecordingPreviewV3(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchVodOfferPreviewV2(
    params: TPreviewServiceGetVodOfferPreviewParamsV2,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<TPreview> {
    return this.executeGuarded(
      "fetchVodOfferPreviewV2",
      async (guardedSignal: AbortSignal): Promise<TPreview> => {
        Log.d(PreviewInteractor.TAG, "fetchVodOfferPreviewV2() called");
        const service: PreviewService = ServiceLocator.getInstance(
          PreviewService,
          await this.buildServicesParams(
            TIVO_SERVICES.PreviewService,
            this.mFeatureArea
          )
        );
        return await service.fetchVodOfferPreviewV2(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  /**
   * Fetches the internal rating name for a given rating key.
   *
   * @param ratingKey - The key corresponding to the desired internal rating.
   * @returns Resolving to the internal rating name as a string.
   */
  public static fetchInternalRatingName(ratingKey: string): string {
    // Passing blank object as getInternalRatingName doesn't require any params
    const internalRatingsService = new InternalRatingsService(
      {} as TServicesParams
    );
    const res: string = internalRatingsService.getInternalRatingName(ratingKey);
    return res;
  }
}
